import type { components } from '../../api/generated/schema'

type Schema = components['schemas']

/**
 * The gallery types are **entirely generated**, and that is worth noting after five
 * domains of hand-transcription: `ImageGalleryDto` is a real return type on every gallery
 * operation, so OpenAPI sees it and the CI drift check covers it. Nothing here is
 * transcribed by hand.
 *
 * The reason is structural rather than lucky. A gallery is a *single-section* resource, so
 * it has no `sections` dictionary to be untyped — the envelope collapses to "here is the
 * whole list", and a whole list is something OpenAPI can describe.
 */
export type Gallery = Schema['ImageGalleryDto']
export type GalleryImage = Schema['GalleryImageDto']
export type LocationImageType = Schema['LocationImageType']

/**
 * Which gallery a screen is showing.
 *
 * Character and location galleries are the same resource behind two routes, and the only
 * differences are the path and whether image *kinds* mean anything — so one descriptor
 * drives one component and one set of hooks rather than two of each.
 */
export type GalleryOwner =
  | { kind: 'character'; characterId: number }
  | { kind: 'location'; campaignId: number; locationId: number }

/** The API path for an owner's gallery. The single place either route is written down. */
export function galleryPath(owner: GalleryOwner): string {
  return owner.kind === 'character'
    ? `/characters/${owner.characterId}/images`
    : `/campaigns/${owner.campaignId}/locations/${owner.locationId}/images`
}

/** A stable cache key fragment for an owner. */
export function galleryOwnerKey(owner: GalleryOwner): readonly (string | number)[] {
  return owner.kind === 'character'
    ? ['character', owner.characterId]
    : ['location', owner.campaignId, owner.locationId]
}

/**
 * Whether image kinds mean anything here.
 *
 * `GalleryImageDto.Type` is `Depiction` / `BattleMap` on a location and **null** on a
 * character, which has no kinds. Branching on the owner rather than on the field keeps a
 * null from reading as "kind unknown" when it means "kinds do not apply".
 */
export function hasImageKinds(owner: GalleryOwner): boolean {
  return owner.kind === 'location'
}

/** The gallery's main image, if it has one. Layout only — `isMain` is the server's word. */
export function mainImage(gallery: Gallery | undefined): GalleryImage | undefined {
  return (gallery?.images ?? []).find((image) => image.isMain === true)
}
